import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { environment } from '@awo-care/shared-auth';
import { EmployeeErrorHandlerService } from './core/services/employee-error-handler.service';
import { GOOGLE_MAPS_API_KEY } from '@solidgroup-agency/ui-dynamic-form';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  MAT_DATE_FORMATS,
  MatDateFormats,
  DateAdapter,
} from '@angular/material/core';
import { CustomGermanDateAdapter } from '@awo-care/utils';
import { provideHttpClient, withXhr } from '@angular/common/http';
import { provideFirebase, USE_EMULATORS } from '@awo-care/shared-firebase';
import { UserService } from './core/services/user.service';
import { provideTicketPortalUserReset } from './core/services/ticket-portal-user-reset';
import { FEEDBACK_WIDGET_CONFIG, FeedbackSubmissionPayload } from '@solidgroup-agency/feedback-widget';
import {
  SG_AUTH,
  SG_FIRESTORE,
  SG_FUNCTIONS,
} from '@solidgroup-agency/firebase-app-monitoring';
import { provideAwoTicketSystem, provideAwoTicketSystemConfig } from './tickets/tickets.providers';
import { Auth } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE, FIREBASE_FUNCTIONS } from '@awo-care/shared-firebase';
// Custom Date Adapter for German format

const DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Custom handler as the main ErrorHandler
    {
      provide: ErrorHandler,
      useClass: EmployeeErrorHandlerService,
    },
    // Add the official global listener
    // This connects the browser's global errors to the ErrorHandler
    provideBrowserGlobalErrorListeners(),

    provideAppInitializer(() => {
      const userService = inject(UserService);
      return userService.loadClaims();
    }),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },

    { provide: LOCALE_ID, useValue: 'de-DE' },

    { provide: DateAdapter, useClass: CustomGermanDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },

    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
    ),
    // Kein Service Worker (#1403): jeder Seitenaufruf holt die neueste Version,
    // nach einem Deploy fehlende Dateien lädt EmployeeErrorHandlerService neu.
    // public/ngsw-worker.js meldet den früheren Worker in alten Browsern ab.
    { provide: GOOGLE_MAPS_API_KEY, useValue: environment.googleMapsAPIKey },

    provideHttpClient(withXhr()),

    // Firebase configuration
    ...provideFirebase(
      {
        ...environment.firebaseConfig,
        appId: environment.appIds.employeeUi,
        measurementId: environment.measurementIds.employeeUi,
      },
      {
        useEmulators: environment.useEmulators,
        useFunctionsEmulatorOnly: environment.useFunctionsEmulatorOnly,
      },
      {
        enableAnalytics: false, // Analytics disabled for employee portal
        enableAppCheck: environment.enableAppCheck,
        enablePerformance: environment.enablePerformance,
        recaptchaSiteKey: environment.recaptchaSiteKey,
        tier: environment.tier,
        isTokenAutoRefreshEnabled: true,
      }
    ),
    // Provide emulator flag for services to inject
    { provide: USE_EMULATORS, useValue: environment.useEmulators },

    // Native-SDK-Instanzen für @solidgroup-agency/firebase-app-monitoring (ab 2.0.0).
    // Die Library injiziert das native firebase-SDK über eigene Tokens. Unsere
    // Tokens halten genau diese Instanzen (inkl. Emulator- und Region-Setup),
    // also durchreichen statt ein zweites Mal initialisieren.
    { provide: SG_FIRESTORE, useExisting: FIREBASE_FIRESTORE },
    { provide: SG_AUTH, useExisting: FIREBASE_AUTH },
    { provide: SG_FUNCTIONS, useExisting: FIREBASE_FUNCTIONS },

    // Ticket System
    provideAwoTicketSystemConfig(),
    ...provideAwoTicketSystem(),

    // Feedback Widget
    ...(environment.ticketPortalUrl ? [provideTicketPortalUserReset()] : []),
    {
      provide: FEEDBACK_WIDGET_CONFIG,
      useFactory: () => {
        const functions = inject(FIREBASE_FUNCTIONS);
        const auth = inject(FIREBASE_AUTH);
        return {
          customerId: 'awo',
          projectId: 'employee-ui',
          screenshotSkipFonts: false,
          // „Meine Anfragen" (#1175): eigene Tickets direkt aus dem CRM, das den
          // ID-Token prüft. Ohne URL bleibt das Widget beim reinen Formular.
          ...(environment.ticketPortalUrl && {
            ticketPortal: {
              apiUrl: environment.ticketPortalUrl,
              getIdToken: async (forceRefresh?: boolean) => {
                const user = auth.currentUser;
                if (!user) throw new Error('Nicht angemeldet');
                return user.getIdToken(forceRefresh);
              },
              getUserFirstName: () =>
                auth.currentUser?.displayName?.split(' ')[0] ?? null,
            },
          }),
          submitFn: async (payload: FeedbackSubmissionPayload) => {
            const callable = httpsCallable(functions, 'submitFeedback');
            await callable(payload);
          },
        };
      },
    },
  ],
};
